import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { IconTestPipe } from '@tabler/icons-react';

import { getAbTestTooltip } from './abTestUtils';
import { ABTestVariantInfo } from 'src/services/insights/types';

const SIZE_CONFIG = {
  medium: { fontSize: '0.75rem', height: 24, iconSize: 14 },
  small: { fontSize: '0.625rem', height: 18, iconSize: 11 },
} as const;

interface VariantPillProps {
  shouldShowOnlyOnActive?: boolean;
  size?: keyof typeof SIZE_CONFIG;
  variant: ABTestVariantInfo;
}

export default function VariantPill({
  shouldShowOnlyOnActive = false,
  size = 'small',
  variant,
}: VariantPillProps) {
  const { t } = useTranslation('solve-insights');
  const { palette } = useTheme();
  const isActive = variant.ab_test_status === 'running';

  if (shouldShowOnlyOnActive && !isActive) {
    return null;
  }

  const getBackgroundColor = () => {
    if (variant.is_control) {
      return palette.colors.teal[500];
    }
    // Map letter suffix to chart index: "Variant A" -> 1, "Variant B" -> 2, etc.
    const match = variant.variant_name.match(/([A-Z])$/);
    const variantIndex = match ? match[1].charCodeAt(0) - 64 : 1; // A=1, B=2, C=3...
    return palette.chart[variantIndex] ?? palette.chart[1];
  };
  const bg = getBackgroundColor();
  const tooltip = getAbTestTooltip(t, variant);
  const { fontSize, height, iconSize } = SIZE_CONFIG[size];

  return (
    <Tooltip arrow title={tooltip}>
      <Chip
        label={
          <span
            style={{
              alignItems: 'center',
              display: 'flex',
              gap: '3px',
            }}
          >
            <IconTestPipe size={iconSize} style={{ display: 'block' }} />
            {variant.variant_name}
          </span>
        }
        size='small'
        sx={{
          backgroundColor: bg,
          color: palette.colors.white,
          fontSize,
          fontWeight: 600,
          height,
          opacity: isActive ? 1 : 0.5,
        }}
      />
    </Tooltip>
  );
}
